import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BookOpen, TrendingUp, Volume2, Eye, Clock, BookMarked, Users, FileText, X, Code, Smartphone, Globe, Monitor } from 'lucide-react';

// WhatsApp Icon Component
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Designer Photo URL
const DESIGNER_PHOTO = "/designer.png";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const [topSurahs, setTopSurahs] = useState([]);
  const [footerTexts, setFooterTexts] = useState({});
  const [showDesignerModal, setShowDesignerModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
        
        // Fetch top surahs
        const surahsResponse = await fetch(`${backendUrl}/api/surahs/top/listened`);
        const surahsData = await surahsResponse.json();
        setTopSurahs(surahsData.slice(0, 10)); // Top 10
        
        // Fetch footer texts
        const textsResponse = await fetch(`${backendUrl}/api/site-texts`);
        const textsData = await textsResponse.json();
        const textsMap = {};
        textsData.forEach(text => {
          if (text.key.startsWith('footer.')) {
            textsMap[text.key] = text.value;
          }
        });
        setFooterTexts(textsMap);
      } catch (error) {
        console.error('Error fetching footer data:', error);
      }
    };
    fetchData();
  }, []);

  const getText = (key, defaultValue = '') => {
    return footerTexts[key] || defaultValue;
  };

  return (
    <footer className="bg-[#5C4033] text-[#E8DCC8] mt-auto">
      {/* Main Footer Content - Hidden on mobile */}
      <div className="max-w-7xl mx-auto px-8 py-16 hidden md:block">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-16">
          {/* Logo and Description */}
          <div className="space-y-6">
            <div className="flex items-center space-x-3">
              <BookOpen className="h-10 w-10 text-[#D5C4A1]" />
              <span className="text-2xl font-bold text-white">{getText('footer.logo_title', 'Kuran-i Kerim')}</span>
            </div>
            <p className="text-base text-[#D5C4A1] leading-relaxed pr-4">
              {getText('footer.description', 'Yuce Kuran-i Kerimi okuyun, dinleyin ve anlayin. Farklı mealler ve hafızlarla Kuran-i Kerime ulasin.')}
            </p>
            {/* Arabic Text */}
            <div className="text-4xl text-[#D5C4A1] mt-6" style={{ fontFamily: '"Traditional Arabic", serif' }}>
              القرآن الكريم
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-bold text-white mb-6">{getText('footer.quick_links_title', 'Hızlı Erişim')}</h3>
            <ul className="space-y-3.5">
              <li>
                <Link to="/namaz-vakitleri" className="text-base text-[#D5C4A1] hover:text-white transition-colors inline-flex items-center gap-2 py-1.5">
                  <Clock className="h-4 w-4" />
                  {getText('footer.link_prayer_times', 'Namaz Vakitleri')}
                </Link>
              </li>
              <li>
                <Link to="/sureler" className="text-base text-[#D5C4A1] hover:text-white transition-colors inline-flex items-center gap-2 py-1.5">
                  <BookMarked className="h-4 w-4" />
                  {getText('footer.link_surahs', 'Sureler')}
                </Link>
              </li>
              <li>
                <Link to="/hafizlar" className="text-base text-[#D5C4A1] hover:text-white transition-colors inline-flex items-center gap-2 py-1.5">
                  <Users className="h-4 w-4" />
                  {getText('footer.link_reciters', 'Hafızlar')}
                </Link>
              </li>
              <li>
                <Link to="/meal" className="text-base text-[#D5C4A1] hover:text-white transition-colors inline-flex items-center gap-2 py-1.5">
                  <FileText className="h-4 w-4" />
                  {getText('footer.link_meals', 'Mealler')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Top Listened Surahs */}
          <div>
            <div className="flex items-center space-x-2 mb-6">
              <TrendingUp className="h-5 w-5 text-[#D5C4A1]" />
              <h3 className="text-xl font-bold text-white">{getText('footer.top_surahs_title', 'En Çok Dinlenen Sureler')}</h3>
            </div>
            <ul className="space-y-2.5">
              {topSurahs.length > 0 ? (
                <>
                  {topSurahs.map((surah, index) => (
                    <li key={surah.id} className="flex items-start space-x-2">
                      <span className="text-[#D5C4A1] font-bold text-sm mt-0.5 w-6">{index + 1}.</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-[#D5C4A1] font-medium flex-1">
                            {surah.name}
                          </span>
                          <button
                            onClick={() => navigate(`/?surah=${surah.slug || surah.id}`)}
                            className="flex items-center gap-1 px-2 py-0.5 text-xs bg-[#6B5744] hover:bg-[#8B7355] text-white rounded transition-colors"
                          >
                            <Eye className="h-3 w-3" />
                            <span>{getText('footer.button_read', 'Oku')}</span>
                          </button>
                          <button
                            onClick={() => navigate(`/?surah=${surah.slug || surah.id}&autoplay=true`)}
                            className="flex items-center gap-1 px-2 py-0.5 text-xs bg-[#6B5744] hover:bg-[#8B7355] text-white rounded transition-colors"
                          >
                            <Volume2 className="h-3 w-3" />
                            <span>{getText('footer.button_listen', 'Dinle')}</span>
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                  <li className="pt-2 border-t border-[#6B5744]">
                    <Link 
                      to="/sureler" 
                      className="text-sm text-[#D5C4A1] hover:text-white transition-colors flex items-center justify-center py-2"
                    >
                      <span className="font-medium">{getText('footer.all_surahs_link', 'Tüm Sureler →')}</span>
                    </Link>
                  </li>
                </>
              ) : (
                <li className="text-sm text-[#B8A894]">{getText('footer.loading_text', 'Yükleniyor...')}</li>
              )}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar - Visible on all devices */}
      <div className="border-t border-[#6B5744]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 md:py-8">
          {/* Mobile Layout - Dua on top */}
          <div className="flex flex-col items-center space-y-2 md:hidden pb-20">
            {/* Dua - First on mobile */}
            <div className="text-xs text-[#D5C4A1] italic text-center">
              Rabbimiz Bizi Doğruluktan Ayırma Ve Bizi Bağışlayanlardansın
            </div>
            
            {/* Copyright */}
            <div className="text-xs text-[#D5C4A1] text-center">
              &copy; {currentYear} Kuranikerim.se Tüm Hakları Saklıdır
            </div>
            
            {/* Designer Credit */}
            <button
              onClick={() => setShowDesignerModal(true)}
              className="text-xs text-[#D5C4A1] hover:text-white transition-colors cursor-pointer flex items-center gap-1"
            >
              <Code className="h-3 w-3" />
              <span>Tasarım & Yazılım: <span className="font-semibold text-white">Orhan ŞEKER</span></span>
            </button>
          </div>
          
          {/* Desktop Layout - Original order */}
          <div className="hidden md:flex md:flex-row md:items-center md:justify-between">
            {/* Left - Copyright */}
            <div className="text-base text-[#D5C4A1]">
              &copy; {currentYear} Kuranikerim.se Tüm Hakları Saklıdır
            </div>
            
            {/* Center - Designer Credit */}
            <button
              onClick={() => setShowDesignerModal(true)}
              className="text-base text-[#D5C4A1] hover:text-white transition-colors cursor-pointer flex items-center gap-2"
            >
              <Code className="h-4 w-4" />
              <span>Tasarım & Yazılım: <span className="font-semibold text-white">Orhan ŞEKER</span></span>
            </button>
            
            {/* Right - Dua */}
            <div className="text-base text-[#D5C4A1] italic text-right">
              Rabbimiz Bizi Doğruluktan Ayırma Ve Bizi Bağışlayanlardansın
            </div>
          </div>
        </div>
      </div>

      {/* Designer Modal */}
      {showDesignerModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowDesignerModal(false)}
          />
          
          {/* Modal */}
          <div className="relative bg-gradient-to-br from-[#F5F0E8] to-[#E8DCC8] rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-[#D5C4A1]">
            {/* Header with Pattern */}
            <div className="bg-[#5C4033] px-6 py-8 relative overflow-hidden">
              {/* Decorative Pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 left-0 w-32 h-32 border-2 border-white rounded-full -translate-x-1/2 -translate-y-1/2" />
                <div className="absolute bottom-0 right-0 w-24 h-24 border-2 border-white rounded-full translate-x-1/2 translate-y-1/2" />
              </div>
              
              {/* Close Button */}
              <button
                onClick={() => setShowDesignerModal(false)}
                className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
              
              {/* Title with Photo */}
              <div className="text-center relative z-10">
                <div className="inline-block w-24 h-24 rounded-full mb-4 shadow-lg overflow-hidden border-4 border-[#E8DCC8]">
                  <img 
                    src={DESIGNER_PHOTO} 
                    alt="Orhan ŞEKER" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <h2 className="text-2xl font-bold text-white">Orhan ŞEKER</h2>
                <p className="text-[#D5C4A1] mt-1">Full Stack Developer & UI/UX Designer</p>
              </div>
            </div>
            
            {/* Content */}
            <div className="px-6 py-6 space-y-4">
              {/* Service Cards - Like the shared design */}
              <div className="grid grid-cols-3 gap-3">
                {/* Mobil Uygulama */}
                <div className="bg-gradient-to-br from-[#FF6B6B] to-[#EE5A5A] rounded-xl p-4 text-center shadow-lg">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-2">
                    <Smartphone className="h-6 w-6 text-white" />
                  </div>
                  <p className="text-white text-xs font-medium">Mobil Uygulama</p>
                </div>
                
                {/* Web Yazılım */}
                <div className="bg-gradient-to-br from-[#4ECDC4] to-[#3DB9B1] rounded-xl p-4 text-center shadow-lg">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-2">
                    <Globe className="h-6 w-6 text-white" />
                  </div>
                  <p className="text-white text-xs font-medium">Web Yazılım</p>
                </div>
                
                {/* Bilişim Hizmetleri */}
                <div className="bg-gradient-to-br from-[#A78BFA] to-[#8B5CF6] rounded-xl p-4 text-center shadow-lg">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-2">
                    <Monitor className="h-6 w-6 text-white" />
                  </div>
                  <p className="text-white text-xs font-medium">Bilişim Hizmetleri</p>
                </div>
              </div>
              
              {/* WhatsApp Button */}
              <a 
                href="https://wa.me/905383519360" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 w-full py-4 bg-[#25D366] hover:bg-[#20BD5A] text-white font-semibold rounded-xl transition-colors shadow-lg"
              >
                <WhatsAppIcon className="h-6 w-6" />
                <span>WhatsApp ile İletişim</span>
              </a>
              
              {/* Footer Message */}
              <div className="text-center text-sm text-[#8B7355] pt-2 border-t border-[#D5C4A1]">
                <p>Bu proje hayırlı işlere vesile olması dileğiyle geliştirilmiştir.</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </footer>
  );
};

export default Footer;
